import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { getResources } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  FileText, Video, BookOpen, FileDown, Download, ExternalLink,
  Play, Loader2, Search
} from "lucide-react";
import { Input } from "@/components/ui/input";

const TABS = [
  { key: "", label: "All", icon: FileText },
  { key: "white_paper", label: "White Papers", icon: FileText },
  { key: "ebook", label: "E-Books", icon: BookOpen },
  { key: "product_video", label: "Product Videos", icon: Video },
  { key: "case_study_video", label: "Case Study Videos", icon: Video },
  { key: "case_study_doc", label: "Case Studies", icon: FileText },
  { key: "brochure", label: "Brochures", icon: FileDown },
];

const TYPE_COLORS = {
  white_paper: "bg-violet-50 text-violet-700 border-violet-200",
  ebook: "bg-emerald-50 text-emerald-700 border-emerald-200",
  product_video: "bg-blue-50 text-blue-700 border-blue-200",
  case_study_video: "bg-amber-50 text-amber-700 border-amber-200",
  case_study_doc: "bg-slate-50 text-slate-700 border-slate-200",
  brochure: "bg-rose-50 text-rose-700 border-rose-200",
};

const TYPE_LABELS = {
  white_paper: "White Paper", ebook: "E-Book", product_video: "Product Video",
  case_study_video: "Case Study Video", case_study_doc: "Case Study", brochure: "Brochure",
};

function isVideo(type) {
  return type === "product_video" || type === "case_study_video";
}

export default function ResourcesPage() {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const params = activeTab ? { resource_type: activeTab } : {};
        const res = await getResources(params);
        setResources(res.data);
      } catch {}
      finally { setLoading(false); }
    };
    setLoading(true);
    load();
  }, [activeTab]);

  const filtered = search
    ? resources.filter(r => r.title.toLowerCase().includes(search.toLowerCase()) || r.description?.toLowerCase().includes(search.toLowerCase()))
    : resources;

  return (
    <div className="min-h-screen bg-white" data-testid="resources-page">
      <Navbar />

      {/* Hero */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-14">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold mb-3">Resources</h1>
          <p className="text-slate-300 text-sm max-w-2xl mx-auto">
            Explore white papers, e-books, product videos, case studies and brochures to learn how ABSOLIN transforms businesses.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-10">
        {/* Tabs + Search */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8 items-start sm:items-center justify-between">
          <div className="flex gap-2 flex-wrap">
            {TABS.map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                  activeTab === t.key
                    ? "bg-[#0066CC] text-white border-[#0066CC]"
                    : "bg-white text-slate-600 border-slate-200 hover:border-[#0066CC] hover:text-[#0066CC]"
                }`} data-testid={`tab-${t.key || "all"}`}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search resources..." value={search} onChange={(e) => setSearch(e.target.value)}
              className="pl-9 placeholder:text-slate-300 h-9 text-sm" data-testid="resource-search" />
          </div>
        </div>

        {/* Resource Grid */}
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-slate-400">
            <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-sm">No resources found{search && " for your search"}. Check back soon!</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map(r => (
              <Card key={r.id} className="border-slate-200 hover:shadow-md transition-shadow group overflow-hidden" data-testid={`resource-card-${r.id}`}>
                {/* Thumbnail */}
                {r.thumbnail_url ? (
                  <div className="h-40 bg-slate-100 overflow-hidden relative">
                    <img src={r.thumbnail_url} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    {isVideo(r.resource_type) && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                          <Play className="w-5 h-5 text-[#0066CC] ml-0.5" />
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="h-32 bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
                    {isVideo(r.resource_type) ? <Video className="w-10 h-10 text-slate-300" /> : <FileText className="w-10 h-10 text-slate-300" />}
                  </div>
                )}

                <CardContent className="p-4">
                  <span className={`inline-block text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border mb-2 ${TYPE_COLORS[r.resource_type] || "bg-slate-50 text-slate-600"}`}>
                    {TYPE_LABELS[r.resource_type] || r.resource_type}
                  </span>
                  <h3 className="font-semibold text-sm text-slate-900 mb-1 line-clamp-2 group-hover:text-[#0066CC] transition-colors">{r.title}</h3>
                  {r.description && <p className="text-xs text-slate-500 line-clamp-2 mb-3">{r.description}</p>}

                  {r.file_url && (
                    <a href={r.file_url} target="_blank" rel="noopener noreferrer" data-testid={`resource-link-${r.id}`}>
                      <Button size="sm" variant="outline" className="w-full text-xs h-8 border-slate-200 hover:border-[#0066CC] hover:text-[#0066CC]">
                        {isVideo(r.resource_type) ? (
                          <><Play className="w-3.5 h-3.5 mr-1.5" /> Watch Now</>
                        ) : (
                          <><Download className="w-3.5 h-3.5 mr-1.5" /> Download</>
                        )}
                        <ExternalLink className="w-3 h-3 ml-auto" />
                      </Button>
                    </a>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
